import { useSelector } from "react-redux";

import { LevelsOfExperience } from "../models/resume/template";
import type { ProgressStatus } from "../types/resume";

/**
 * Where Education sits relative to Work History.
 *
 * The builder leads with whichever section the reader has more to say about:
 * someone with no jobs yet starts with education, everyone else starts with work
 * history. Every list that shows the two steps — the sidebar rail, the mobile
 * dots, the resume list — and every Continue/Skip target between them reads the
 * order from here, so they cannot drift out of sync with each other again.
 *
 * The level is chosen once on `/resume/select-experience` and kept in
 * `localStorage.yearsOfExperience`, mirrored into `TemplateSelectionReducer`.
 *
 * It is deliberately not read off the resume: the backend destructures
 * `yearsOfExperience` out of the create dto and never persists it (see the
 * regular creation flow in `resume-journal.service.ts`), so a fetched ledger
 * cannot answer this question. That makes the level a per-browser setting rather
 * than a per-resume one.
 */

/** The one level that leads with education — and the fallback when none is set. */
export const NO_EXPERIENCE = "No Experience";

/** Whether `yearsOfExperience` puts education ahead of work history. */
export function isEducationFirst(yearsOfExperience?: string | null): boolean {
	return (yearsOfExperience ?? NO_EXPERIENCE) === NO_EXPERIENCE;
}

/**
 * The stored level, for callers outside React — the store's own initialiser and
 * `NavigationHandler`, which is constructed fresh on every render.
 */
export function readEducationFirst(): boolean {
	return isEducationFirst(localStorage.getItem("yearsOfExperience"));
}

interface CategorisedTemplate {
	templateName?: string;
}

interface ExperienceCategory {
	name: string;
	/** Templates recommended for this level only. */
	templateList: CategorisedTemplate[];
}

/**
 * The level a saved resume was built at, recovered from its template.
 *
 * The backend never stores `yearsOfExperience`, but it does store `templateId`,
 * and the recommended-template lists in `models/resume/template.js` are disjoint
 * across levels — `fresher_template_1` belongs to No Experience alone, while
 * `fresher_template` and `template_1` belong only to the levels above it. That
 * makes the template a durable stand-in for the level: it survives cleared
 * storage and follows the resume onto another device.
 *
 * Premium templates are offered at every level, so they identify nothing and
 * return `undefined` here — the caller falls back to the stored level.
 */
export function levelFromTemplate(templateId?: string | null): string | undefined {
	if (!templateId) return undefined;

	const categories: ExperienceCategory[] = new LevelsOfExperience().listOfExperience;
	const matches = categories.filter((category) =>
		category.templateList?.some((template) => template.templateName === templateId),
	);

	// No match means a premium or retired template; several means the template is
	// recommended on both sides of the split. Neither answers the question.
	const orders = new Set(matches.map((category) => isEducationFirst(category.name)));
	if (orders.size !== 1) return undefined;

	return matches[0].name;
}

interface TemplateSelectionState {
	TemplateSelectionReducer?: {
		selectedTemplate?: { yearsOfExperience?: string };
	};
}

/**
 * The level as components should read it. Subscribing to the store means picking
 * a different level re-renders the rail, rather than leaving it on the order the
 * page first mounted with.
 */
export function useEducationFirst(): boolean {
	return useSelector((state: TemplateSelectionState) =>
		isEducationFirst(state.TemplateSelectionReducer?.selectedTemplate?.yearsOfExperience),
	);
}

/** One of the two steps this module orders. */
export interface OrderedStep {
	/** The progress-rail row, which is always the summary screen. */
	section: string;
	title: string;
	progressKey: keyof ProgressStatus;
}

const EDUCATION_STEP: OrderedStep = {
	section: "education-summary",
	title: "Education",
	progressKey: "EDUCATION",
};

const WORK_HISTORY_STEP: OrderedStep = {
	section: "work-history-summary",
	title: "Work History",
	progressKey: "EXPERIENCE",
};

/** Education and Work History in the order this reader is asked for them. */
export function orderedMiddleSteps(educationFirst: boolean): OrderedStep[] {
	return educationFirst ? [EDUCATION_STEP, WORK_HISTORY_STEP] : [WORK_HISTORY_STEP, EDUCATION_STEP];
}

/**
 * The screen that owns education. The summary redirects to the entry form on an
 * empty list, so sending a first-time reader there would only flicker.
 */
export function educationEntry(educationCount: number): string {
	return educationCount > 0 ? "education-summary" : "education-information";
}

/** The screen that owns work history, on the same rule as `educationEntry`. */
export function workHistoryEntry(workHistoryCount: number): string {
	return workHistoryCount > 0 ? "work-history-summary" : "work-history";
}

/** What is already filled in, which decides summary-vs-entry-form. */
export interface SectionCounts {
	educationCount: number;
	workHistoryCount: number;
}

/** The section after Personal Information. */
export function nextAfterPersonalInfo(educationFirst: boolean, counts: SectionCounts): string {
	return educationFirst ? educationEntry(counts.educationCount) : workHistoryEntry(counts.workHistoryCount);
}

/** The section after Education: Work History when it has not been asked for yet. */
export function nextAfterEducation(educationFirst: boolean, counts: SectionCounts): string {
	return educationFirst ? workHistoryEntry(counts.workHistoryCount) : "skills";
}

/** The section after Work History: Education when it has not been asked for yet. */
export function nextAfterWorkHistory(educationFirst: boolean, counts: SectionCounts): string {
	return educationFirst ? "skills" : educationEntry(counts.educationCount);
}

/**
 * How many nodes `NavigationHandler.next()` steps over to reach the section after
 * this one.
 *
 * Both flows chain a section's entry form, details form and summary as separate
 * nodes, so jumping straight to a summary means skipping the forms in front of
 * it. Skipping nothing lands on the entry form, which is what a reader with an
 * empty list wants.
 */
export function skipFromEducationSummary(educationFirst: boolean, workHistoryCount: number): number {
	// education-summary -> work-history -> work-details -> work-history-summary.
	// The experienced flow puts skills next instead, one node away.
	if (!educationFirst) return 0;
	return workHistoryCount > 0 ? 2 : 0;
}

/** The mirror of `skipFromEducationSummary`, for the experienced flow. */
export function skipFromWorkHistorySummary(educationFirst: boolean, educationCount: number): number {
	// work-history-summary -> education-information -> education-summary.
	// The fresher flow puts skills next instead, one node away.
	if (educationFirst) return 0;
	return educationCount > 0 ? 1 : 0;
}

/**
 * Walking back out of a summary screen.
 *
 * The reader is on a summary because that section already has entries, so "back"
 * means the previous *section*, not the entry form they filled in to get here —
 * stepping onto that form would look like the resume had been thrown away.
 */
export function previousBeforeEducation(educationFirst: boolean, counts: SectionCounts): string {
	return educationFirst ? "contact-information" : workHistoryEntry(counts.workHistoryCount);
}

/** The mirror of `previousBeforeEducation`. */
export function previousBeforeWorkHistory(educationFirst: boolean, counts: SectionCounts): string {
	return educationFirst ? educationEntry(counts.educationCount) : "contact-information";
}

/** The title of whatever `previousBeforeEducation` resolves to, for the button. */
export function previousBeforeEducationTitle(educationFirst: boolean): string {
	return educationFirst ? "Personal Information" : "Work History";
}

/** The title of whatever `previousBeforeWorkHistory` resolves to. */
export function previousBeforeWorkHistoryTitle(educationFirst: boolean): string {
	return educationFirst ? "Education" : "Personal Information";
}

/**
 * The title of whatever `nextAfterWorkHistory` resolves to, for the button. Named
 * off the same flag the target is, so the label cannot advertise a section the
 * button does not open.
 */
export function nextAfterWorkHistoryTitle(educationFirst: boolean): string {
	return educationFirst ? "Skills" : "Education";
}

/**
 * The section before Skills — whichever of the two middle steps this reader was
 * asked for second.
 */
export function previousBeforeSkillsTitle(educationFirst: boolean): string {
	return educationFirst ? "Work History" : "Education";
}

/**
 * The section after Skills. Both flows end the same way, so this does not turn on
 * the order. The flow node calls the screen "Brief Summary"; the rail and the
 * sidebar call it Professional Summary, which is the name the reader has already
 * seen by the time they reach this button.
 */
export const NEXT_AFTER_SKILLS_TITLE = "Professional Summary";

/** Where a back button goes, and the section name it names on the way. */
export interface PreviousStep {
	section: string;
	title: string;
}

/**
 * Walking back out of an entry form.
 *
 * Entries already saved mean the reader reached the form from this section's own
 * summary and expects to land back on their list. An empty list means they were
 * sent here by the section before it, which is where back belongs instead.
 */
export function previousFromEducationForm(educationFirst: boolean, counts: SectionCounts): PreviousStep {
	if (counts.educationCount > 0) return { section: "education-summary", title: "Education" };

	return {
		section: previousBeforeEducation(educationFirst, counts),
		title: previousBeforeEducationTitle(educationFirst),
	};
}

/** The mirror of `previousFromEducationForm`. */
export function previousFromWorkHistoryForm(educationFirst: boolean, counts: SectionCounts): PreviousStep {
	if (counts.workHistoryCount > 0) return { section: "work-history-summary", title: "Work History" };

	return {
		section: previousBeforeWorkHistory(educationFirst, counts),
		title: previousBeforeWorkHistoryTitle(educationFirst),
	};
}

/**
 * How many nodes `NavigationHandler.previous()` steps over on the way there.
 *
 * Counted on the flow both levels share, so the numbers hold either way round:
 * education-summary sits one node behind its own entry form, and
 * work-history-summary two behind its entry and details forms.
 */
export const SKIP_BACK_FROM_EDUCATION_SUMMARY = 1;
export const SKIP_BACK_FROM_WORK_HISTORY_SUMMARY = 2;
